import { defineStore } from 'pinia'
import apiHelper from '@/utils/apiHelper'
import { API_URL } from '@/constants/api'
import commonUtil from '@/utils/commonUtil'
import { $dialog } from '@/plugins/dialog-alert'
import { $alert } from '@/plugins/simple-alert'


export type DownloadStatus = 'downloading' | 'retrying' | 'done' | 'error' | 'cancelled'

export interface DownloadTask {
  key: string
  dataNo: number
  fileNo: number
  fileName: string
  fileType: string
  totalBytes: number
  receivedBytes: number
  writtenBytes: number
  status: DownloadStatus
  errorMessage: string
  resumable: boolean
  retryAttempt: number
  etag: string
  downloadNo?: number
}

interface DownloadState {
  tasks: Record<string, DownloadTask>
  // 탑툴바 DownloadManagerModal 오픈 상태. App.vue의 다운로드 버튼뿐 아니라
  // NotificationDropdown의 다운로드 완료/실패 알림 클릭에서도 열어야 해서
  // 컴포넌트 로컬 상태가 아니라 스토어에 둔다.
  isManagerOpen: boolean
  // 1회성 신호: true면 다음에 매니저 팝업이 열릴 때 다운로드 이력을 펼친 채로 보여준다.
  // 알림 클릭으로 들어온 항목은 새로고침 후엔 이번 세션 목록(tasks)에서 사라져 있을 수
  // 있어(페이지 새로고침 시 초기화됨), 서버 조회 기반인 이력 쪽을 바로 보여주기 위함.
  openManagerWithHistoryExpanded: boolean
  // 서버의 다운로드 이력이 바뀐 시점(시작 기록 생성, 완료/실패/취소 PATCH 응답 후)마다 1씩 증가.
  // DownloadHistoryTable이 이 값을 보고 이력을 다시 불러온다 — 화면의 task 상태는 PATCH보다
  // 먼저 바뀌므로 task 상태 대신 서버 반영 이후의 이 신호를 기준으로 갱신해야 한다.
  historyVersion: number
}

// AbortController/스트림 핸들처럼 직렬화 불가능하고 내부 슬롯에 의존하는 네이티브 객체는
// Pinia의 reactive state 안에 두지 않는다 — Vue의 reactive Proxy로 감싸면 이런 객체들의
// 메서드가 내부적으로 기대하는 원본 this 바인딩이 깨져서 "Illegal invocation" 류 에러가
// 날 수 있다. 그래서 실행 핸들(AbortController)과 저장 파일 핸들은 모듈 스코프의 일반
// Map에 따로 둔다.
const controllers = new Map<string, AbortController>()

// FileSystemFileHandle(showSaveFilePicker 결과) — 재시도/이어받기 때 저장 대화상자를 다시
//띄우지 않고 같은 파일에 이어 쓰기 위해 task 완료/취소 전까지 보관한다.
const fileHandles = new Map<string, any>()

function taskKey(dataNo: number, fileNo: number): string {
  return `${dataNo}:${fileNo}`
}

// File System Access API는 TS 기본 dom lib에 아직 완전히 포함돼 있지 않을 수 있어
// any로 캐스팅해서 다룬다. 지원 브라우저(Chromium 계열)에서만 window.showSaveFilePicker가
// 함수로 존재한다.
function getSaveFilePicker(): ((options: { suggestedName: string }) => Promise<any>) | undefined {
  const w = window as unknown as { showSaveFilePicker?: (options: { suggestedName: string }) => Promise<any> }
  return typeof w.showSaveFilePicker === 'function' ? w.showSaveFilePicker : undefined
}

export function supportsStreamingSave(): boolean {
  return !!getSaveFilePicker()
}

// File System Access API 미지원 브라우저(Firefox, Safari 등)는 saveViaBlob 경로를 타는데,
// 이 방식은 다운로드가 끝날 때까지 파일 전체를 메모리(Blob)에 들고 있다가 한 번에 저장한다.
// 파일이 너무 크면 탭이 느려지거나 메모리 부족으로 실패할 수 있어, 이 경우에만 사전 안내한다.
const LARGE_FILE_WARNING_BYTES = 1024 * 1024 * 1024 // 1GB

export const SINGLE_FILE_DOWNLOAD_LIMIT_BYTES = 1 * 1024 * 1024 * 1024 // 1GB

// task.receivedBytes(progress) 반영 최소 간격 — 청크마다 갱신하면 대용량 파일에서
// 초당 수백~수천 회 재렌더링이 발생해 메인 스레드를 막는다 (saveViaFileSystemAccess 참고).
const PROGRESS_UPDATE_INTERVAL_MS = 150

// 자동 재시도 정책 — VPN 환경 등 일시적 네트워크 문제를 감안해 지수 백오프로 4회까지
// 화면 개입 없이 재시도한다. 다 소진되면 status='error'로 전환하고(FS Access 경로라면
// resumable=true) 수동 "이어받기" 버튼을 노출한다.
const MAX_RETRY_ATTEMPTS = 4
const RETRY_BACKOFF_MS = [2000, 5000, 15000, 30000]


function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const timer = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(new DOMException('Aborted', 'AbortError'))
      },
      { once: true },
    )
  })
}

async function requestDownloadSlot(dataNo: number, fileNo: number): Promise<number | undefined> {
  const res = await apiHelper.post<{ downloadNo: number }>(API_URL.DATA.DOWNLOAD_START(dataNo, fileNo))
  if (!res || res.errorCode !== 0 || res.data?.downloadNo === undefined) return undefined
  return res.data.downloadNo
}

async function reportDownloadStatus(
  dataNo: number,
  fileNo: number,
  downloadNo: number | undefined,
  status: 'DONE' | 'ERROR' | 'CANCELLED',
): Promise<number | undefined> {
  if (downloadNo === undefined) return undefined
  try {
    const res = await apiHelper.patch<{ downloadsCount?: number }>(
      API_URL.DATA.DOWNLOAD_STATUS(dataNo, fileNo, downloadNo),
      { status },
    )
    if (res && res.errorCode === 0) return res.data?.downloadsCount
  } catch {
    // 무시 — 위 주석 참고
  }
  return undefined
}

function confirmLargeFileDownload(fileName: string, totalBytes: number): Promise<boolean> {
  return new Promise((resolve) => {
    const sizeLabel = commonUtil.formatBytes(totalBytes, 1)
    $dialog.confirm(
      '대용량 파일 다운로드 안내',
      `현재 브라우저는 저장 위치를 지정해 순차적으로 받는 기능을 지원하지 않아, 다운로드가 끝날 때까지 파일 전체(${sizeLabel})를 메모리에 담아두었다가 저장합니다. 파일이 커서 브라우저가 느려지거나 다운로드가 실패할 수 있습니다.\n\nChrome, Edge 등 최신 브라우저 사용을 권장합니다.\n\n"${fileName}" 다운로드를 계속하시겠습니까?`,
      () => resolve(true),
      () => resolve(false),
    )
  })
}

export const useDownloadStore = defineStore('download', {
  persist: false,

  state: (): DownloadState => ({
    tasks: {},
    isManagerOpen: false,
    openManagerWithHistoryExpanded: false,
    historyVersion: 0,
  }),

  getters: {
    activeTasks(state): DownloadTask[] {
      return Object.values(state.tasks).filter((t) => t.status === 'downloading' || t.status === 'retrying')
    },
    hasActiveDownload(state): boolean {
      return Object.values(state.tasks).some((t) => t.status === 'downloading' || t.status === 'retrying')
    },
  },

  actions: {
    getTask(dataNo: number, fileNo: number): DownloadTask | undefined {
      return this.tasks[taskKey(dataNo, fileNo)]
    },

    // dataNo/fileNo/fileName/fileType/totalBytes(=file.fileSize)는 호출부(드롭다운)가
    // 이미 갖고 있는 DataFileResponse에서 그대로 넘겨준다 — 별도 조회 불필요.
    async startDownload(
      dataNo: number,
      fileNo: number,
      fileName: string,
      fileType: string,
      totalBytes: number,
      // 완료 후 다운로드 로그 응답(downloadsCount)을 호출부(페이지의 통계 카드 등)에
      // 반영하고 싶을 때 쓰는 콜백. 스토어는 어느 화면의 어떤 로컬 상태를 갱신해야 하는지
      // 알 수 없으니, 필요하면 호출부가 이 콜백으로 직접 받아가게 한다.
      onDownloadLogged?: (downloadsCount: number) => void,
    ) {
      const key = taskKey(dataNo, fileNo)
      if (this.tasks[key]?.status === 'downloading' || this.tasks[key]?.status === 'retrying') return // 중복 클릭 방지

      // 서버와 동일한 상한을 프론트에서 먼저 걸러 불필요한 왕복을 줄인다(실제 강제는 서버가 함).
      if (totalBytes > SINGLE_FILE_DOWNLOAD_LIMIT_BYTES) {
        $alert.error(
          `1GB를 초과하는 파일은 다운로드할 수 없습니다. (${commonUtil.formatBytes(totalBytes, 1)})`,
        )
        return
      }

      const savePicker = getSaveFilePicker()
      if (!savePicker && totalBytes >= LARGE_FILE_WARNING_BYTES) {
        const proceed = await confirmLargeFileDownload(fileName, totalBytes)
        if (!proceed) return
      }

      const downloadNo = await requestDownloadSlot(dataNo, fileNo)
      if (downloadNo === undefined) return
      this.historyVersion++ // 서버에 DOWNLOADING 이력이 새로 생김

      // 완전히 새로 시작하는 것이므로 이전에 남아있던 파일 핸들(있다면)은 버린다 —
      // 이어받기가 아니라 재다운로드이므로 처음부터 다시 저장 대화상자를 띄운다.
      fileHandles.delete(key)

      this.tasks[key] = {
        key,
        dataNo,
        fileNo,
        fileName,
        fileType,
        totalBytes,
        receivedBytes: 0,
        writtenBytes: 0,
        status: 'downloading',
        errorMessage: '',
        resumable: false,
        retryAttempt: 0,
        etag: '',
        downloadNo,
      }

      await this.runDownloadLoop(key, dataNo, fileNo, fileName, savePicker, onDownloadLogged)
    },

    // 자동 재시도를 모두 소진하고 status='error', resumable=true로 남은 항목을 이어받는다.
    // startDownload와 달리 task.writtenBytes/etag를 그대로 유지하고, 저장 대화상자도
    // 다시 띄우지 않는다(fileHandles에 남아있는 handle을 재사용).
    async resumeDownload(dataNo: number, fileNo: number, onDownloadLogged?: (downloadsCount: number) => void) {
      const key = taskKey(dataNo, fileNo)
      const task = this.tasks[key]
      if (!task || task.status !== 'error' || !task.resumable) return

      const savePicker = getSaveFilePicker()
      if (!savePicker) return // resumable=true는 애초에 FS Access 지원 브라우저에서만 세팅됨

      const downloadNo = await requestDownloadSlot(dataNo, fileNo)
      if (downloadNo === undefined) return
      this.historyVersion++ // 서버에 DOWNLOADING 이력이 새로 생김
      task.downloadNo = downloadNo

      task.status = 'downloading'
      task.errorMessage = ''
      task.retryAttempt = 0

      await this.runDownloadLoop(key, dataNo, fileNo, task.fileName, savePicker, onDownloadLogged)
    },

    // startDownload/resumeDownload가 공유하는 실제 다운로드 루프. presigned URL 발급 →
    // fetch → 저장을 한 번의 "시도"로 보고, AbortError가 아닌 모든 실패에 대해
    // MAX_RETRY_ATTEMPTS까지 지수 백오프로 재시도한다. 재시도 시 매번 presigned URL을
    // 새로 발급받아 만료(기본 15분) 문제를 피하고, writtenBytes가 있으면 Range 헤더로
    // 이어받기를 시도한다.
    async runDownloadLoop(
      key: string,
      dataNo: number,
      fileNo: number,
      fileName: string,
      savePicker: ((options: { suggestedName: string }) => Promise<any>) | undefined,
      onDownloadLogged?: (downloadsCount: number) => void,
    ) {
      const task = this.tasks[key]
      if (!task) return

      const controller = new AbortController()
      controllers.set(key, controller)

      // 완료/실패/취소 상태를 서버에 반영하고, 반영된 뒤에 이력 갱신 신호를 보낸다.
      const report = (status: 'DONE' | 'ERROR' | 'CANCELLED') =>
        reportDownloadStatus(dataNo, fileNo, task.downloadNo, status).finally(() => {
          this.historyVersion++
        })

      const markDone = () => {
        task.status = 'done'
        report('DONE').then((downloadsCount) => {
          if (downloadsCount !== undefined) onDownloadLogged?.(downloadsCount)
        })
      }

      let attempt = 0
      try {
        for (;;) {
          try {
            if (savePicker && task.totalBytes > 0 && task.writtenBytes >= task.totalBytes && fileHandles.has(key)) {
              await this.finalizeFileSystemAccess(key)
              markDone()
              return
            }

            const res = await apiHelper.get<{ url: string }>(API_URL.DATA.DOWNLOAD_URL(dataNo, fileNo))
            if (!res || res.errorCode !== 0 || !res.data?.url) {
              throw new Error('다운로드 URL 발급에 실패했습니다.')
            }

            let targetUrl = res.data.url
            try {
              if (targetUrl.startsWith('http://') || targetUrl.startsWith('https://')) {
                const parsed = new URL(targetUrl)
                const isPrivate = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.|127\.|localhost)/.test(parsed.hostname)
                if (isPrivate && parsed.hostname !== window.location.hostname) {
                  targetUrl = API_URL.DATA.RAW_FILE(dataNo, fileNo)
                }
              }
            } catch {
              // URL 파싱 실패 시 원본 유지
            }

            const rangeStart = task.writtenBytes
            // priority: 'low' — 같은 스토리지 호스트로 가는 썸네일/파일 요청이 HTTP/1.1
            // 호스트당 연결 슬롯(보통 6개)을 두고 대용량 다운로드와 경합하는 걸 완화하기
            // 위함. 크롬의 네트워크 스케줄러가 슬롯을 배정할 때 이 요청의 우선순위를
            // 낮춰서, 슬롯이 부족할 땐 다른(우선순위 기본값) 요청이 먼저 배정되게 한다.
            // 미지원 브라우저에서는 그냥 무시되는 표준 옵션이라 안전하다.
            const fetchInit: RequestInit = { signal: controller.signal, priority: 'low' }
            if (rangeStart > 0) {
              fetchInit.headers = { Range: `bytes=${rangeStart}-` }
            }

            let response: Response
            try {
              response = await fetch(targetUrl, fetchInit)
              if (!response.ok && targetUrl !== API_URL.DATA.RAW_FILE(dataNo, fileNo)) {
                targetUrl = API_URL.DATA.RAW_FILE(dataNo, fileNo)
                response = await fetch(targetUrl, fetchInit)
              }
            } catch (fetchErr) {
              if (targetUrl !== API_URL.DATA.RAW_FILE(dataNo, fileNo)) {
                targetUrl = API_URL.DATA.RAW_FILE(dataNo, fileNo)
                response = await fetch(targetUrl, fetchInit)
              } else {
                throw fetchErr
              }
            }

            if (!response.ok || !response.body) {
              if (response.status === 416 && rangeStart > 0 && savePicker && fileHandles.has(key)) {
                await this.finalizeFileSystemAccess(key)
                markDone()
                return
              }
              throw new Error(`다운로드 요청이 실패했습니다. (status: ${response.status})`)
            }

            let effectiveStart = rangeStart
            if (rangeStart > 0) {
              if (response.status === 206) {
                const etag = response.headers.get('ETag') || ''
                if (task.etag && etag && etag !== task.etag) {
                  effectiveStart = 0
                  task.writtenBytes = 0
                }
              } else {
                effectiveStart = 0
                task.writtenBytes = 0
              }
            }
            if (!task.etag) {
              const etag = response.headers.get('ETag')
              if (etag) task.etag = etag
            }

            if (savePicker) {
              await this.saveViaFileSystemAccess(key, fileName, response, controller.signal, savePicker, effectiveStart)
            } else {
              await this.saveViaBlob(key, fileName, response)
            }

            markDone()
            return
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
              task.status = 'cancelled'
              fileHandles.delete(key)
              report('CANCELLED')
              return
            }

            attempt += 1
            if (attempt > MAX_RETRY_ATTEMPTS) {
              task.status = 'error'
              task.resumable = !!savePicker
              task.errorMessage = err instanceof Error ? err.message : '다운로드 중 오류가 발생했습니다.'
              report('ERROR')
              return
            }

            task.status = 'retrying'
            task.retryAttempt = attempt
            try {
              const backoff = RETRY_BACKOFF_MS[Math.min(attempt - 1, RETRY_BACKOFF_MS.length - 1)] ?? 2000
              await sleep(backoff, controller.signal)
            } catch {
              task.status = 'cancelled'
              fileHandles.delete(key)
              report('CANCELLED')
              return
            }
            task.status = 'downloading'
          }
        }
      } finally {
        controllers.delete(key)
      }
    },

    async saveViaFileSystemAccess(
      key: string,
      fileName: string,
      response: Response,
      signal: AbortSignal,
      savePicker: (options: { suggestedName: string }) => Promise<any>,
      startPosition: number,
    ) {
      let handle = fileHandles.get(key)
      if (!handle) {
        handle = await savePicker({ suggestedName: fileName })
        fileHandles.set(key, handle)
      }
      const writable = await handle.createWritable({ keepExistingData: true })

      const task = this.tasks[key]

      let writtenSoFar = startPosition
      let lastFlushAt = 0
      const reader = response.body!.getReader()

      if (task) task.writtenBytes = writtenSoFar

      try {
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          if (value && value.byteLength > 0) {
            await writable.write({ type: 'write', position: writtenSoFar, data: value })
            writtenSoFar += value.byteLength
            if (task) {
              task.writtenBytes = writtenSoFar
              const now = performance.now()
              if (now - lastFlushAt >= PROGRESS_UPDATE_INTERVAL_MS) {
                task.receivedBytes = writtenSoFar
                lastFlushAt = now
              }
            }
          }
        }
        if (task) task.receivedBytes = writtenSoFar
        await writable.write({ type: 'truncate', size: writtenSoFar })
        await writable.close()
        fileHandles.delete(key)
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          try {
            await writable.abort()
          } catch {
            // 이미 닫혔거나 abort 불가 상태 — 무시
          }
          fileHandles.delete(key)
        }
        throw err
      }
    },


    async finalizeFileSystemAccess(key: string) {
      const handle = fileHandles.get(key)
      if (!handle) {
        throw new Error('저장 중이던 파일 정보를 찾을 수 없습니다. 다시 다운로드해 주세요.')
      }
      const task = this.tasks[key]
      const writable = await handle.createWritable({ keepExistingData: true })
      await writable.write({ type: 'truncate', size: task?.writtenBytes ?? 0 })
      await writable.close()
      fileHandles.delete(key)
    },

    async saveViaBlob(key: string, fileName: string, response: Response) {
      const reader = response.body!.getReader()
      const chunks: BlobPart[] = []
      const task = this.tasks[key]

      let receivedSoFar = 0
      let lastFlushAt = 0
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        if (value) {
          chunks.push(value)
          receivedSoFar += value.byteLength
          const now = performance.now()
          if (task && now - lastFlushAt >= PROGRESS_UPDATE_INTERVAL_MS) {
            task.receivedBytes = receivedSoFar
            lastFlushAt = now
          }
        }
      }
      if (task) task.receivedBytes = receivedSoFar

      const blob = new Blob(chunks)
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(objectUrl)
    },

    cancelDownload(dataNo: number, fileNo: number) {
      controllers.get(taskKey(dataNo, fileNo))?.abort()
    },

    // 완료/실패/취소된 항목을 드롭다운에서 지우고(원래 크기 표시로 복귀) 다시 받을 수 있게 함
    clearTask(dataNo: number, fileNo: number) {
      const key = taskKey(dataNo, fileNo)
      fileHandles.delete(key)
      delete this.tasks[key]
    },

    openManagerWithHistory() {
      this.isManagerOpen = true
      this.openManagerWithHistoryExpanded = true
    },
  },
})
